import { ErrorHandler, Injectable, OnDestroy } from '@angular/core';
import { TranslocoService, Translation } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import {
  applyNwcTerminologyToText,
  applyNwcTerminologyToTranslation,
} from 'app/shared/utils/nwc-terminology.util';

@Injectable({
  providedIn: 'root',
})
export class NwcTerminologyService implements OnDestroy {
  private originalTranslations = new Map<string, Translation>();
  private loadingLanguages = new Set<string>();
  private langChangesSubscription?: Subscription;
  private pendingLangChangeTimeout?: ReturnType<typeof setTimeout>;
  private readonly ignoredLangChanges = new Set<string>();

  constructor(
    private translocoService: TranslocoService,
    private errorHandler: ErrorHandler,
  ) {}

  start(): void {
    if (this.langChangesSubscription) {
      return;
    }

    this.langChangesSubscription = this.translocoService.langChanges$.subscribe((lang) => {
      if (this.ignoredLangChanges.delete(lang)) {
        return;
      }

      this.clearPendingLangChange();
      this.pendingLangChangeTimeout = setTimeout(() => {
        this.pendingLangChangeTimeout = undefined;
        this.applyForCurrentUser(lang);
      });
    });
  }

  applyForCurrentUser(lang = this.translocoService.getActiveLang()): void {
    const currentTranslation = this.getNonEmptyTranslation(
      this.translocoService.getTranslation(lang),
    );
    if (!currentTranslation) {
      this.loadTranslationOnce(lang);
      return;
    }

    if (!this.originalTranslations.has(lang)) {
      this.originalTranslations.set(lang, this.cloneTranslation(currentTranslation));
    }

    const originalTranslation = this.originalTranslations.get(lang);
    if (!originalTranslation) {
      return;
    }

    const nextTranslation = this.isCurrentUserNwc()
      ? applyNwcTerminologyToTranslation(originalTranslation)
      : this.cloneTranslation(originalTranslation);

    this.ignoredLangChanges.add(lang);
    this.translocoService.setTranslation(nextTranslation, lang, { merge: false });
  }

  ngOnDestroy(): void {
    this.langChangesSubscription?.unsubscribe();
    this.langChangesSubscription = undefined;
    this.clearPendingLangChange();
    this.loadingLanguages.clear();
    this.ignoredLangChanges.clear();
  }

  formatTextForCurrentUser(value: string): string {
    return this.isCurrentUserNwc() ? applyNwcTerminologyToText(value) : value;
  }

  private isCurrentUserNwc(): boolean {
    try {
      const userData = localStorage.getItem('user-data');
      return userData ? JSON.parse(userData)?.unifier_project_adapter === 'nwc' : false;
    } catch (error) {
      this.errorHandler.handleError(error);
      return false;
    }
  }

  private cloneTranslation(translation: Translation): Translation {
    return JSON.parse(JSON.stringify(translation));
  }

  private getNonEmptyTranslation(translation: Translation | undefined): Translation | undefined {
    return translation && Object.keys(translation).length > 0 ? translation : undefined;
  }

  private loadTranslationOnce(lang: string): void {
    if (this.loadingLanguages.has(lang)) {
      return;
    }

    this.loadingLanguages.add(lang);
    this.translocoService.load(lang).subscribe({
      next: () => {
        this.loadingLanguages.delete(lang);
        if (this.getNonEmptyTranslation(this.translocoService.getTranslation(lang))) {
          this.applyForCurrentUser(lang);
        }
      },
      error: (error) => {
        this.loadingLanguages.delete(lang);
        this.errorHandler.handleError(error);
      },
      complete: () => this.loadingLanguages.delete(lang),
    });
  }

  private clearPendingLangChange(): void {
    if (this.pendingLangChangeTimeout === undefined) {
      return;
    }

    clearTimeout(this.pendingLangChangeTimeout);
    this.pendingLangChangeTimeout = undefined;
  }
}
